import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import api from '../api';

/**
 * /admin/points-log — audit trail of admin_grant + admin_debit entries.
 * Read-only. Filter by reason and target user email.
 */
export default function AdminPointsLog() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState(null);
  const [reason, setReason] = useState('admin_all');
  const [emailFilter, setEmailFilter] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(emailFilter.trim()), 300);
    return () => clearTimeout(t);
  }, [emailFilter]);

  useEffect(() => {
    const params = { limit: 200 };
    if (reason !== 'admin_all') params.reason = reason;
    if (debounced) params.user_email = debounced;
    api.get('/admin/points-log', { params })
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => setEntries([]));
  }, [reason, debounced]);

  const totalGranted = useMemo(() => {
    if (!entries) return 0;
    return entries.filter((e) => e.delta > 0).reduce((a, b) => a + b.delta, 0);
  }, [entries]);
  const totalDebited = useMemo(() => {
    if (!entries) return 0;
    return Math.abs(entries.filter((e) => e.delta < 0).reduce((a, b) => a + b.delta, 0));
  }, [entries]);

  return (
    <AppShell>
      <div className="apl-shell" data-testid="admin-points-log">
        <div className="apl-header">
          <button className="back-btn" onClick={() => navigate('/admin')} data-testid="apl-back-btn" aria-label="Back">
            <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <h1 className="apl-heading">Points Ledger <span>Audit Log</span></h1>
        </div>
        <p className="apl-sub">Every admin grant + debit. Read-only. Includes admin identity, target user, delta, note, and timestamp.</p>

        <div className="apl-stats">
          <div className="apl-stat" data-testid="apl-total-granted">
            <div className="apl-stat-label">Total granted (in view)</div>
            <div className="apl-stat-value up">+{totalGranted.toLocaleString()} pts</div>
          </div>
          <div className="apl-stat" data-testid="apl-total-debited">
            <div className="apl-stat-label">Total debited (in view)</div>
            <div className="apl-stat-value down">−{totalDebited.toLocaleString()} pts</div>
          </div>
          <div className="apl-stat" data-testid="apl-net-flow">
            <div className="apl-stat-label">Net flow</div>
            <div className={`apl-stat-value ${totalGranted - totalDebited >= 0 ? 'up' : 'down'}`}>
              {totalGranted - totalDebited >= 0 ? '+' : ''}{(totalGranted - totalDebited).toLocaleString()} pts
            </div>
          </div>
        </div>

        <div className="apl-filters">
          <div className="apl-tabs" data-testid="apl-tabs">
            {['admin_all', 'admin_grant', 'admin_debit'].map((r) => (
              <button
                key={r}
                className={`apl-tab${reason === r ? ' active' : ''}`}
                onClick={() => setReason(r)}
                data-testid={`apl-tab-${r}`}
              >
                {r === 'admin_all' ? 'All' : r === 'admin_grant' ? 'Grants' : 'Debits'}
              </button>
            ))}
          </div>
          <input
            className="apl-search"
            placeholder="Filter by target user email…"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            data-testid="apl-email-filter"
          />
        </div>

        {entries === null && <div className="apl-empty">Loading…</div>}
        {entries && entries.length === 0 && (
          <div className="apl-empty" data-testid="apl-empty">
            No admin grants or debits match your filter.
          </div>
        )}
        {entries && entries.length > 0 && (
          <div className="apl-list" data-testid="apl-list">
            <div className="apl-row apl-header-row">
              <div className="apl-c-date">When</div>
              <div className="apl-c-target">Target user</div>
              <div className="apl-c-delta">Delta</div>
              <div className="apl-c-reason">Reason</div>
              <div className="apl-c-note">Note</div>
              <div className="apl-c-admin">By admin</div>
            </div>
            {entries.map((e) => (
              <div key={e.id} className="apl-row" data-testid={`apl-row-${e.id}`}>
                <div className="apl-c-date">{new Date(e.created_at).toLocaleString()}</div>
                <div className="apl-c-target">
                  <div className="apl-target-name">{e.target.display_name || '—'}</div>
                  <div className="apl-target-email">{e.target.email}</div>
                </div>
                <div className={`apl-c-delta ${e.delta > 0 ? 'up' : 'down'}`}>
                  {e.delta > 0 ? '+' : ''}{e.delta.toLocaleString()} pts
                </div>
                <div className="apl-c-reason">
                  <span className={`apl-pill ${e.reason}`}>{e.reason === 'admin_grant' ? 'Grant' : 'Debit'}</span>
                </div>
                <div className="apl-c-note">{e.note || <span className="apl-muted">—</span>}</div>
                <div className="apl-c-admin">{e.admin.email || <span className="apl-muted">—</span>}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
